{ MidiFile, nextTrackEvent } from @danielx/lib

MidiSong from ./song.civet
{ beatOfMeasure } from ./measures.civet
{ noteIdCounter } from ./note-index.civet
type { PracticeMetadata } from ./practice-metadata.civet

// Import Synthesia's sidecar metadata (.synthesia XML) into our in-MIDI
// practice metadata. Songs are keyed by UniqueId — the base64 MD5 of the
// music file — and the Parts / FingerHints instruction strings address notes
// by their physical order within each TRACK of the original file. Our song
// model flattens tracks, so all conversion happens here against the raw
// bytes, landing on track-independent note ids (tick:channel:pitch).
//
// Reference: https://github.com/Synthesia-LLC/metadata-editor/wiki
//
// The legacy HandParts field (pre-Synthesia-10 proprietary note-thumbprint
// hashes) is not interpretable and is ignored; Parts supersedes it.

export type SynthesiaSong =
  uniqueId: string
  title?: string
  subtitle?: string
  composer?: string
  arranger?: string
  copyright?: string
  license?: string
  madeFamousBy?: string
  tags?: string[]
  rating?: number
  difficulty?: number
  backgroundImage?: string
  bookmarks: {measure: number, name?: string}[]
  parts?: string
  fingerHints?: string

// Real-world .synthesia files key songs by lowercase hex MD5 (measured
// against the Midiano database); the wiki describes base64. Accept either,
// normalizing to hex; an unrecognizable id passes through as an opaque key.
export normalizeMd5Id := (id: string): string ->
  return id.toLowerCase() if /^[0-9a-fA-F]{32}$/.test id
  try
    decoded := atob id
    if decoded.length is 16
      return [...decoded].map (ch) ->
        ch.charCodeAt(0).toString(16).padStart 2, "0"
      .join ""
  id

export parseSynthesiaXML := (xml: string): SynthesiaSong[] ->
  //@ts-ignore DOMParser comes from the window in tests
  Parser := globalThis.DOMParser ?? window.DOMParser
  doc := new Parser().parseFromString xml, "text/xml"

  if doc.querySelector "parsererror"
    throw new Error "Not a valid .synthesia file"

  root := doc.documentElement
  unless root?.tagName is "SynthesiaMetadata"
    throw new Error "Not a valid .synthesia file"

  [...doc.querySelectorAll "Songs > Song"].flatMap (el) ->
    uniqueId := el.getAttribute "UniqueId"
    return [] unless uniqueId

    str := (name: string) -> el.getAttribute(name) or undefined
    int := (name: string) ->
      v := el.getAttribute name
      if v? then parseInt(v, 10) else undefined

    bookmarks := (str("Bookmarks") ?? "").split ";"
      .map (entry) ->
        [m, ...rest] := entry.split ","
        measure := parseInt m, 10
        return unless measure >= 1
        name := rest.join(",").trim() or undefined
        {measure, name}
      .filter (b) => b?

    [{
      uniqueId
      title: str "Title"
      subtitle: str "Subtitle"
      composer: str "Composer"
      arranger: str "Arranger"
      copyright: str "Copyright"
      license: str "License"
      madeFamousBy: str "MadeFamousBy"
      tags: str("Tags")?.split(";").filter (t) -> t.length
      rating: int "Rating"
      difficulty: int "Difficulty"
      backgroundImage: str "BackgroundImage"
      bookmarks: bookmarks as {measure: number, name?: string}[]
      parts: str "Parts"
      fingerHints: str "FingerHints"
    }]

// --- Note enumeration against the raw file ---

type TrackNote =
  tick: number
  id: string

// noteOns per track, in each track's physical event order, with our
// track-independent ids. Duplicate (tick,channel,pitch) get a :k suffix in
// flattened tick order so ids stay unique.
enumerateTrackNotes := (midiBytes: Uint8Array): TrackNote[][] ->
  file := new MidiFile midiBytes
  trackNotes: TrackNote[][] := file.tracks.map -> []
  nextId := noteIdCounter()
  tick .= 0

  while e := nextTrackEvent file
    tick += e.deltaTime
    status := e.type
    continue unless (status & 0xf0) is 0x90
    // running status leaves the status byte out of data
    args := if e.data[0] is status then e.data.subarray(1) else e.data
    continue unless args[1] > 0 // velocity 0 is a release

    trackNotes[e.track].push
      tick: tick
      id: nextId `${tick}:${status & 0x0f}:${args[0]}`

  trackNotes

// --- Instruction walker shared by Parts and FingerHints ---

// Tokens: `tN:` selects a (zero-based) track, `mN:` moves the current track's
// cursor to (one-based) measure N, anything else is a single symbol handed to
// the caller. Whitespace is ignored.
walkInstructions := (
  source: string
  tracks: TrackNote[][]
  measureStartTick: (measure: number) => number
  symbol: (ch: string, state: {track: number, cursors: number[]}) => void
) ->
  state := {track: 0, cursors: tracks.map -> 0}

  tokens := source.matchAll /([tm])(\d+):|(\S)/g
  for [, jump, num, ch] of tokens
    if jump is "t"
      state.track = parseInt num, 10
    else if jump is "m"
      startTick := measureStartTick parseInt num, 10
      notes := tracks[state.track] ?? []
      i .= 0
      i++ while i < notes.length and notes[i].tick < startTick
      state.cursors[state.track] = i
    else
      symbol ch, state
  return

// Parts: L/R/B/X assign the next note (or `LN` the next N notes, `LA` every
// note in the track) to left hand / right hand / background / discard;
// `-` leaves notes as they were.
applyParts := (
  source: string
  tracks: TrackNote[][]
  measureStartTick: (measure: number) => number
): Map<string, string> ->
  hands := new Map<string, string>

  // Re-tokenize with counts attached: symbol plus optional repeat/A suffix
  tokens := source.matchAll /([tm])(\d+):|([LRBX-])(\d+|A)?/g
  state := {track: 0, cursors: tracks.map -> 0}

  for [, jump, num, letter, count] of tokens
    if jump is "t"
      state.track = parseInt num, 10
    else if jump is "m"
      startTick := measureStartTick parseInt num, 10
      notes := tracks[state.track] ?? []
      i .= 0
      i++ while i < notes.length and notes[i].tick < startTick
      state.cursors[state.track] = i
    else
      notes := tracks[state.track] ?? []
      if count is "A"
        // from the cursor to the end of the track — the whole track when no
        // mN: preceded it, "this measure onward" when one did
        unless letter is "-"
          for i of [state.cursors[state.track]...notes.length]
            hands.set notes[i].id, letter
        state.cursors[state.track] = notes.length
      else
        n := if count? then parseInt(count, 10) else 1
        for _ of [0...n]
          cursor := state.cursors[state.track]
          break unless cursor < notes.length
          hands.set notes[cursor].id, letter unless letter is "-"
          state.cursors[state.track] = cursor + 1

  hands

// FingerHints: one symbol per note in track order — 1-5 left fingers, 6-9
// and 0 right fingers 1-5, `-` skips, `s` chains a substitution onto the
// previous note ("1s2": land on L1, switch to L2 while held).
fingerCode := (digit: string): string ->
  d := parseInt digit, 10
  if 1 <= d <= 5
    `L${d}`
  else if d is 0
    "R5"
  else
    `R${d - 5}`

applyFingerHints := (
  source: string
  tracks: TrackNote[][]
  measureStartTick: (measure: number) => number
): Record<string, string> ->
  fingering: Record<string, string> := {}
  let lastId: string?
  substitution .= false

  walkInstructions source, tracks, measureStartTick, (ch, state) ->
    if ch is "s"
      substitution = true
      return
    if ch is "-"
      state.cursors[state.track]++
      lastId = undefined
      substitution = false
      return
    return unless /\d/.test ch

    if substitution and lastId?
      fingering[lastId] += "s" + fingerCode ch
      substitution = false
      return

    notes := tracks[state.track] ?? []
    cursor := state.cursors[state.track]
    return unless cursor < notes.length
    {id} := notes[cursor]
    fingering[id] = fingerCode ch
    lastId = id
    state.cursors[state.track] = cursor + 1
    substitution = false

  fingering

// --- Conversion ---

// Convert one .synthesia Song entry into our practice metadata, resolving
// its track-scoped instruction strings against the raw MIDI bytes it was
// authored for.
export synthesiaToPracticeMetadata := (midiBytes: Uint8Array, entry: SynthesiaSong): PracticeMetadata ->
  tracks := enumerateTrackNotes midiBytes

  // Measure boundaries straight from the time-signature map (song
  // .measureNumbers is display state and only refreshes on signature edits);
  // the walk itself is the shared one in measures.civet
  song := new MidiSong
  song.init midiBytes
  measureStartTick := (measure: number) ->
    beatOfMeasure(song.timeSignatures(), measure) * song.ticksPerBeat

  hands := if entry.parts
    applyParts entry.parts, tracks, measureStartTick
  else
    new Map<string, string>

  byHand := (letter: string) ->
    ids: string[] := []
    hands.forEach (hand, id) ->
      ids.push id if hand is letter
    ids

  lh := byHand "L"
  rh := byHand "R"
  discard := byHand "X"

  parts: PracticeMetadata["parts"] := []
  parts.push {name: "RH", notes: rh} if rh.length
  parts.push {name: "LH", notes: lh} if lh.length

  fingering := if entry.fingerHints
    applyFingerHints entry.fingerHints, tracks, measureStartTick
  else
    {}

  lastMeasure := do
    endTick := song.beatLength() * song.ticksPerBeat
    n .= 1
    n++ while measureStartTick(n + 1) < endTick
    n
  sorted := entry.bookmarks.slice().sort (a, b) -> a.measure - b.measure
  sections := sorted.map ({measure, name}, i) ->
    name: name
    startMeasure: measure
    endMeasure: (sorted[i + 1]?.measure ?? lastMeasure + 1) - 1

  meta: PracticeMetadata := {version: 2, sourceMd5: entry.uniqueId, parts}
  meta.title = entry.title if entry.title?
  meta.difficulty = entry.difficulty if entry.difficulty?
  meta.discard = discard if discard.length
  meta.sections = sections if sections.length
  meta.fingering = fingering if Object.keys(fingering).length
  meta
